import { useEffect, useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePersonasQuery } from "@/api/personas";
import {
  deleteScheduledJob,
  createScheduledJob,
  updateScheduledJob,
  CreateJob,
  UpdateJob,
  Job,
  jobsQueries,
} from "@/api/scheduledJobs";
import { apiClient } from "@/api/api";
import { AddJobDialog } from "@/components/automate/AddJobDialog";
import { EditJobDialog } from "@/components/automate/EditJobDialog";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  LoaderCircle,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  CalendarIcon,
  Bot,
  User,
  ExternalLink,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import YouTubeTableCell from "@/components/YouTubeTableCell";
import { PlanContentDialog } from "./automate/PlanContentModal";
import { productQueries } from "@/api/product";
import { format } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { automationQueries } from "@/api/automations";

/**
 * If the job has an execution_time_in_tz + timezone, figure out
 * the next local occurrence. Then format for display/sort.
 */
function getNextLocalDate(job: Job): Date {
  const { execution_time_in_tz, timezone } = job;
  if (!execution_time_in_tz || !timezone) {
    // "far future" date ensures it sorts last if no valid time
    return new Date(8640000000000000);
  }
  try {
    const [hhStr, mmStr] = execution_time_in_tz.split(":");
    const hh = parseInt(hhStr, 10) || 0;
    const mm = parseInt(mmStr, 10) || 0;
    const now = new Date();

    // Pretend "today hh:mm" is in job.timezone => convert => UTC => local
    const jobTzDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hh,
      mm,
      0,
    );
    const asUtc = fromZonedTime(jobTzDate, timezone);
    const localDate = toZonedTime(
      asUtc,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    );

    // if localDate is in the past, bump to tomorrow
    if (localDate.getTime() <= Date.now()) {
      localDate.setDate(localDate.getDate() + 1);
    }
    return localDate;
  } catch (err) {
    console.error("Error constructing next local date for job:", err);
    // fallback big date
    return new Date(8640000000000000);
  }
}

function getLocalDisplay(job: Job): string {
  const localDate = getNextLocalDate(job);
  return format(localDate, "HH:mm");
}

function sortJobsByLocalTime(jobs: Job[]): Job[] {
  return [...jobs].sort(
    (a, b) => getNextLocalDate(a).getTime() - getNextLocalDate(b).getTime(),
  );
}

/**
 * If this is a tweet posting job, display the tweet text if available.
 * Otherwise, return metadata.topic, metadata.query, metadata.youtube_url,
 * or show tweet_id if present.
 */
function getTopicInfo(job: Job): string {
  const metadata = job.metadata || {};

  // If this is a tweet posting job, display the tweet text if available
  if (
    (job.product_sku === "twitter-posting-post-existing-tweet" &&
      metadata.text) ||
    (job.product_sku === "instagram-posting-post-existing-content" &&
      metadata.instagram?.text)
  ) {
    // Truncate tweet text to 50 characters with ellipsis if needed
    return metadata.text.length > 50
      ? `${metadata.text.substring(0, 125)}...`
      : metadata.text;
  }

  // Return other metadata or default value
  return (
    metadata.topic ||
    metadata.query ||
    metadata.youtube_url ||
    (metadata.tweet_id ? `Tweet ID: ${metadata.tweet_id}` : "Recent News")
  );
}

/**
 * Fetches tweet text for jobs with tweet_id using the Twitter API
 */
/**
 * Fetches tweet text for jobs with tweet_id using the Twitter API
 */
async function fetchTweetTextForJobs(jobs: Job[]): Promise<Job[]> {
  // Create a deep copy of jobs to avoid modifying the original array
  const updatedJobs = structuredClone(jobs);

  const jobsWithTweetIds = updatedJobs.filter(
    (job) =>
      job.product_sku === "twitter-posting-post-existing-tweet" ||
      job.product_sku === "instagram-posting-post-existing-content" ||
      job.metadata?.tweet_id,
  );

  if (jobsWithTweetIds.length === 0) {
    return updatedJobs;
  }

  try {
    // Process each job with tweet_id one at a time
    for (const job of jobsWithTweetIds) {
      console.log(job);
      const tweetId = job.metadata?.tweet_id;
      if (!tweetId) continue;

      try {
        // Use the API client to fetch the tweet

        const response = await apiClient.get(
          `/api/tweets/${job.metadata?.tweet_id}`,
        );
        const tweet = response.data;

        if (tweet && tweet.text) {
          // Find and update the job in our copy of jobs
          const jobIndex = updatedJobs.findIndex(
            (j) => j.job_id === job.job_id,
          );
          if (jobIndex !== -1) {
            updatedJobs[jobIndex].metadata = {
              ...updatedJobs[jobIndex].metadata,
              text: tweet.text,
            };
          }
        }
      } catch (tweetError: any) {
        console.error(`Error fetching tweet ${tweetId}:`, tweetError);
        // Log detailed error information for debugging
        if (tweetError.response) {
          console.error(
            "Error response:",
            tweetError.response.status,
            tweetError.response.data,
          );
        }
      }

      // Add a small delay to avoid rate limiting
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  } catch (error) {
    console.error("Error fetching tweet texts:", error);
  }

  return updatedJobs;
}

// Just for the summary stats
const JOB_TYPES = {
  TEXT_POSTS: [
    "twitter-posting-create-news-post",
    "twitter-posting-create-worldview-post",
  ],
  MEDIA_POSTS: [
    "twitter-posting-create-image-post",
    "twitter-posting-create-multimodal-post",
  ],
  THREADS: ["twitter-threads-custom-topic", "twitter-threads-youtube-threader"],
  KOL_ENGAGEMENT: ["twitter-engagement-engage-with-kols"],
};

function getJobTypeCounts(jobs: Job[]) {
  return {
    textPosts: jobs.filter((job) =>
      JOB_TYPES.TEXT_POSTS.includes(job.product_sku),
    ).length,
    mediaPosts: jobs.filter((job) =>
      JOB_TYPES.MEDIA_POSTS.includes(job.product_sku),
    ).length,
    threads: jobs.filter((job) => JOB_TYPES.THREADS.includes(job.product_sku))
      .length,
    kolEngagement: jobs.filter((job) =>
      JOB_TYPES.KOL_ENGAGEMENT.includes(job.product_sku),
    ).length,
  };
}

interface ScheduledJobsProps {
  selectedPersonaId: string;
}

function ScheduledJobs({ selectedPersonaId }: ScheduledJobsProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // 1) Persona + product queries
  const { data: personas = [] } = usePersonasQuery();
  const {
    data: products = [],
    isLoading: isLoadingProducts,
    error: productFetchError,
  } = useQuery(productQueries.products());

  // 2) Jobs query with tweet text enhancement
  const {
    data: rawJobs = [],
    isFetching: isFetchingJobs,
    error: personaJobsQueryError,
  } = useQuery(jobsQueries.persona(selectedPersonaId));

  // State for tweets loading and management
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isFetchingTweets, setIsFetchingTweets] = useState(false);
  const [selectedTweet, setSelectedTweet] = useState<string | null>(null);

  // Fetch tweet text
  useEffect(() => {
    async function loadJobsWithTweetText() {
      if (rawJobs.length === 0) {
        setJobs([]);
        return;
      }

      try {
        setIsFetchingTweets(true);
        const jobsWithTweetText = await fetchTweetTextForJobs(rawJobs);
        setJobs(jobsWithTweetText);
      } catch (error) {
        console.error("Error loading tweets:", error);
        setJobs(rawJobs); // Fallback to raw jobs if tweet fetching fails
      } finally {
        setIsFetchingTweets(false);
      }
    }

    loadJobsWithTweetText();
  }, [rawJobs]);

  // 3) Also fetch "generalConfig" to see if they have an automation config:
  const { data: generalConfig, isLoading: isLoadingConfig } = useQuery(
    automationQueries.generalConfig(selectedPersonaId),
  );

  // local states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  // The new "AI Scheduler" button + dialog
  const [openAiSchedulerDialog, setOpenAiSchedulerDialog] = useState(false);

  const loading =
    deleteInProgress ||
    isFetchingJobs ||
    isLoadingProducts ||
    isLoadingConfig ||
    isFetchingTweets;

  // handle errors
  useEffect(() => {
    if (personaJobsQueryError) {
      toast({
        description: "Error fetching scheduled jobs. Please try again later.",
      });
    }
    if (productFetchError) {
      toast({
        description: "Error fetching products data.",
      });
    }
  }, [personaJobsQueryError, productFetchError, toast]);

  // handle job CRUD
  const handleDeleteJob = async (agentId: string, jobId: string) => {
    setDeleteInProgress(true);
    try {
      await deleteScheduledJob(agentId, jobId);
      queryClient.invalidateQueries(jobsQueries.persona(selectedPersonaId));
      toast({ description: "Job deleted successfully" });
    } catch (error) {
      console.error("Error deleting job:", error);
      toast({ description: "Error deleting job. Please try again later." });
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleAddJobSave = async (jobData: CreateJob) => {
    try {
      await createScheduledJob(jobData);
      setOpenAddDialog(false);
      queryClient.invalidateQueries(jobsQueries.persona(selectedPersonaId));
      toast({ description: "Job created successfully" });
    } catch (error) {
      console.error("Error creating job:", error);
      toast({ description: "Error creating job. Please try again later." });
      throw error;
    }
  };

  const handleEditJobSave = async (updatedJobData: UpdateJob) => {
    try {
      await updateScheduledJob(updatedJobData);
      queryClient.invalidateQueries(jobsQueries.persona(selectedPersonaId));
      toast({ description: "Job updated successfully" });
      setEditingJob(null);
    } catch (error) {
      console.error("Error updating job:", error);
      toast({ description: "Error updating job. Please try again later." });
      throw error;
    }
  };

  // Merge product info
  const jobsWithProducts = useMemo(() => {
    if (!products || !jobs) return jobs;
    return jobs.map((job) => {
      const sku = job.product_sku;
      if (!sku) {
        return {
          ...job,
          productName: "Unknown Product",
          productFamily: "N/A",
        };
      }
      const found = products.find((p) => p.product_sku === sku);
      if (!found) {
        return {
          ...job,
          productName: `Unrecognized: ${sku}`,
          productFamily: "N/A",
        };
      }
      return {
        ...job,
        productName: found.name,
        productFamily: found.product_family || "N/A",
      };
    });
  }, [jobs, products]);

  // Sort by next local occurrence
  const sortedJobs = useMemo(
    () => sortJobsByLocalTime(jobsWithProducts),
    [jobsWithProducts],
  );

  // Summaries
  const jobCounts = useMemo(() => getJobTypeCounts(jobs), [jobs]);

  // Decide if AI Scheduler button should be disabled
  const hasGeneralConfig = Boolean(generalConfig);
  // If there's no generalConfig => disable + show tooltip.

  return (
    <Card className="p-6">
      {/* Summary Stats */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <Card className="flex flex-col items-center justify-center p-4">
          <div className="text-2xl font-semibold text-primary">
            {jobCounts.textPosts}
          </div>
          <div className="text-sm text-muted-foreground">Text Posts</div>
        </Card>
        <Card className="flex flex-col items-center justify-center p-4">
          <div className="text-2xl font-semibold text-primary">
            {jobCounts.mediaPosts}
          </div>
          <div className="text-sm text-muted-foreground">Media Posts</div>
        </Card>
        <Card className="flex flex-col items-center justify-center p-4">
          <div className="text-2xl font-semibold text-primary">
            {jobCounts.threads}
          </div>
          <div className="text-sm text-muted-foreground">Threads</div>
        </Card>
        <Card className="flex flex-col items-center justify-center p-4">
          <div className="text-2xl font-semibold text-primary">
            {jobCounts.kolEngagement}
          </div>
          <div className="text-sm text-muted-foreground">KOL Engagement</div>
        </Card>
      </div>

      {/* Header row with 2 buttons */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Scheduled Posts</h2>
        </div>

        <div className="flex items-center gap-2">
          {/* AI Scheduler Button with a tooltip if no config */}
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              {/* We wrap the disabled button in a <div> so tooltip can still show */}
              <TooltipTrigger asChild>
                <div className="inline-block">
                  <Button
                    variant="outline"
                    onClick={() => setOpenAiSchedulerDialog(true)}
                    disabled={!hasGeneralConfig}
                  >
                    AI Scheduler
                  </Button>
                </div>
              </TooltipTrigger>

              {/* Show the tooltip only if config is missing */}
              {!hasGeneralConfig && (
                <TooltipContent>
                  <p>
                    Create an Autonomous Agent Configuration to use AI
                    scheduling
                  </p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          {/* Existing Add Job */}
          <Button onClick={() => setOpenAddDialog(true)} className="gap-2">
            <PlusIcon className="h-5 w-5" />
            Add Job
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <LoaderCircle className="text-primary h-12 w-12 animate-spin" />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Creator</TableHead>
              <TableHead className="w-[80px]">Job Type</TableHead>
              <TableHead className="w-[160px] text-center">
                Next Run&nbsp;(
                {Intl.DateTimeFormat().resolvedOptions().timeZone})
              </TableHead>
              <TableHead className="w-[180px]">Product Family</TableHead>
              <TableHead className="w-[220px]">Product</TableHead>
              <TableHead className="w-[100px]">Tone</TableHead>
              <TableHead className="w-[350px]">Topic</TableHead>
              <TableHead className="text-right w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedJobs.map((job) => {
              const jobType =
                job.recurring_or_one_off === "recurring"
                  ? "Recurring"
                  : "One-Off";
              const localTimeDisplay = getLocalDisplay(job);
              const tone = job.metadata?.tone || "";
              const topic = getTopicInfo(job);
              const productName =
                (job as any).productName || job.product_sku || "N/A";
              const productFamily = (job as any).productFamily || "N/A";
              const isTweetJob =
                job.product_sku === "twitter-posting-post-existing-tweet";
              const hasTweetText = isTweetJob && job.metadata?.text;

              return (
                <TableRow key={job.job_id}>
                  <TableCell className="flex justify-center">
                    {job.generated_by === "ai" ? (
                      <Bot className="h-7 w-7 text-primary" />
                    ) : (
                      <User className="h-7 w-7 text-muted-foreground" />
                    )}
                  </TableCell>

                  <TableCell>
                    <Badge
                      variant={
                        jobType === "Recurring" ? "secondary" : "outline"
                      }
                    >
                      {jobType}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-center">
                    {localTimeDisplay}
                  </TableCell>
                  <TableCell>{productFamily}</TableCell>
                  <TableCell>
                    <Badge variant="default" className="font-normal">
                      {productName}
                    </Badge>
                  </TableCell>

                  <TableCell>{tone.split(" - ")[0] || "N/A"}</TableCell>

                  <TableCell className="break-words">
                    {isTweetJob ? (
                      <div className="flex items-center space-x-2">
                        <div className="italic text-sm flex-1">
                          {hasTweetText
                            ? `"${getTopicInfo(job)}"`
                            : `Tweet ID: ${job.metadata?.tweet_id || "Unknown"}`}
                        </div>
                        {hasTweetText && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setSelectedTweet(job.metadata?.text || null)
                            }
                            className="p-1 h-auto"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ) : job.metadata?.youtube_url ? (
                      <YouTubeTableCell url={job.metadata.youtube_url} />
                    ) : (
                      topic
                    )}
                  </TableCell>

                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              className="text-primary"
                              variant="ghost"
                              onClick={() => setEditingJob(job)}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Edit Job</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              className="text-destructive"
                              onClick={() =>
                                handleDeleteJob(job.agent_id, job.job_id)
                              }
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Delete Job</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {openAddDialog && (
        <AddJobDialog
          onClose={() => setOpenAddDialog(false)}
          onSave={handleAddJobSave}
          selectedPersonaId={selectedPersonaId}
        />
      )}

      {editingJob && (
        <EditJobDialog
          onClose={() => setEditingJob(null)}
          onSave={handleEditJobSave}
          jobData={editingJob}
        />
      )}

      {/* Full Tweet Dialog */}
      <Dialog
        open={!!selectedTweet}
        onOpenChange={(open) => !open && setSelectedTweet(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Tweet Content</DialogTitle>
            <DialogDescription>
              The full text of the tweet to be posted
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 px-2 border rounded-md bg-muted/20">
            {selectedTweet}
          </div>
        </DialogContent>
      </Dialog>

      {/* The AI Scheduler Dialog (PlanContentDialog or something) */}
      {openAiSchedulerDialog && (
        <PlanContentDialog
          onClose={() => setOpenAiSchedulerDialog(false)}
          personas={personas}
          selectedPersonaId={selectedPersonaId}
        />
      )}
    </Card>
  );
}

export default ScheduledJobs;
